import { Injectable } from '@angular/core';
import { ProjectionComputedResults } from '../models/projection.model';
import { computeFederalTax, getMarginalRate, SE_TAX_RATE } from '../constants/federal-tax-brackets';
import { computeCapGainsTax, getCapGainsRate, computeNIIT } from '../constants/cap-gains-brackets';
import { StateRateService } from '../tpiq-services/state-rate.service';

/**
 * Frontend tax calculation engine for the Tax Projections feature.
 *
 * Computes Taxable Income and Total Tax from a scenario's field values
 * using public IRS bracket data. Strategy application stays on the backend.
 *
 * Calculation flow (mirrors 1040 instructions):
 * 1. Total Income = sum of all income fields
 * 2. Adjustments = sum of all adjustment fields
 * 3. AGI = Total Income - Adjustments
 * 4. Deductions = itemized deductions entered by user
 * 5. QBI deduction (if applicable)
 * 6. Taxable Income = AGI - Deductions - QBI - Additional Deductions
 * 7. Federal Tax = progressive bracket computation
 * 8. + SE Tax (15.3% on self-employment income)
 * 9. + Cap Gains Tax (preferential rates on long-term gains + qualified dividends)
 * 10. + NIIT (3.8% on investment income above threshold)
 * 11. - Credits
 * 12. = Federal Tax Due
 * 13. State Tax = state bracket computation
 * 14. Total Tax = Federal + State
 */
@Injectable({
  providedIn: 'root'
})
export class TaxCalcService {

  constructor(private stateRateService: StateRateService) {}

  /**
   * Main computation entry point.
   * Takes a map of fieldKey -> amount and returns computed tax results.
   */
  computeAll(
    fields: { [fieldKey: string]: number },
    filingStatus: string,
    homeState: string,
    projectionYear: number
  ): ProjectionComputedResults {
    const f = (key: string) => fields[key] || 0;

    // ─── Step 1: Total Income ──────────────────────────────────────
    const totalIncome = this.sumFields(fields, [
      'w2_wages', 'ira_distributions', 'pensions_annuities', 'social_security',
      'roth_conversion', 'interest_taxable', 'dividends_ordinary',
      'cap_gains_short_term', 'cap_gains_long_term',
      'taxable_refund', 'alimony_received', 'business_income',
      'other_gains_losses', 'rental_royalties_etc', 'farm_income',
      'unemployment_comp', 'other_income', 'foreign_earned_income'
    ]);

    // ─── Step 2: Adjustments ───────────────────────────────────────
    const totalAdjustments = this.sumFields(fields, [
      'adj_educator_expenses', 'adj_reservist_expenses', 'adj_hsa',
      'adj_moving_armed_forces', 'adj_se_tax', 'adj_sep_simple',
      'adj_se_health_ins', 'adj_early_withdrawal', 'adj_alimony_paid',
      'adj_ira_deduction', 'adj_student_loan', 'adj_archer_msa',
      'adj_other_deductions'
    ]);

    // ─── Step 3: AGI ───────────────────────────────────────────────
    const agi = totalIncome - totalAdjustments;

    // ─── Step 4: Deductions (itemized) ─────────────────────────────
    // SALT cap: total state/local taxes capped at $10,000 ($5,000 for MFS)
    const saltCap = filingStatus === 'Married Filing Separately (MFS)' ? 5000 : 10000;
    const totalStateLocal = f('scha_salt') + f('scha_real_estate_tax') + f('scha_personal_prop_tax');
    const deductibleSL = Math.min(totalStateLocal, saltCap);

    const totalItemizedDeductions =
      f('scha_medical') +
      deductibleSL +
      f('scha_other_deductible_tax') +
      f('scha_mortgage_interest') +
      f('scha_charity') +
      f('scha_ira_deduction') +
      f('scha_casualty_theft') +
      f('scha_other_itemized');

    // ─── Step 5: Additional Deductions (Schedule 1-A) ──────────────
    const totalAdditionalDeductions = this.sumFields(fields, [
      'sch1a_no_tax_tips', 'sch1a_no_tax_overtime',
      'sch1a_car_loan_interest', 'sch1a_enhanced_seniors'
    ]);

    // ─── Step 6: Taxable Income ────────────────────────────────────
    const totalDeductions = totalItemizedDeductions + totalAdditionalDeductions;
    const taxableIncome = Math.max(0, agi - totalDeductions);

    // ─── Step 7: Federal Tax on ordinary income ────────────────────
    // Separate ordinary income from preferential income
    const qualifiedDividends = f('dividends_qualified');
    const longTermCapGains = f('cap_gains_long_term');
    const preferentialIncome = qualifiedDividends + longTermCapGains;

    // Ordinary taxable income (excluding preferential income)
    const ordinaryTaxableIncome = Math.max(0, taxableIncome - preferentialIncome);
    const federalOrdinaryTax = computeFederalTax(ordinaryTaxableIncome, filingStatus);

    // ─── Step 8: Capital gains tax on preferential income ──────────
    const capGainsTax = computeCapGainsTax(preferentialIncome, taxableIncome, filingStatus);

    // ─── Step 9: Self-employment tax ───────────────────────────────
    const businessIncome = f('business_income');
    const seTax = businessIncome > 0 ? Math.round(businessIncome * (SE_TAX_RATE / 100) * 100) / 100 : 0;

    // ─── Step 10: NIIT ─────────────────────────────────────────────
    const netInvestmentIncome = f('interest_taxable') + f('dividends_ordinary') +
      f('cap_gains_short_term') + f('cap_gains_long_term') +
      f('rental_royalties_etc');
    const niit = computeNIIT(netInvestmentIncome, agi, filingStatus);

    // ─── Step 11: Credits ──────────────────────────────────────────
    // Child Tax Credit: $2,000 per qualifying child (2025-2026)
    const childTaxCreditDeps = f('child_tax_credit_deps');
    const childTaxCredit = childTaxCreditDeps * 2000;
    // Other dependents credit: $500 per qualifying dependent
    const otherDependents = f('other_dependents');
    const otherDependentsCredit = otherDependents * 500;
    const totalCredits = childTaxCredit + otherDependentsCredit;

    // ─── Step 12: Total Federal Tax ──────────────────────────────
    const totalFederalTax = Math.max(0, federalOrdinaryTax + capGainsTax + seTax + niit - totalCredits);

    // ─── Step 13: State Tax ────────────────────────────────────────
    const stateRate = homeState
      ? this.getStateRate(homeState, filingStatus, taxableIncome, projectionYear)
      : 0;
    const stateTax = Math.round(taxableIncome * (stateRate / 100) * 100) / 100;

    // ─── Step 14: Total Tax ────────────────────────────────────────
    const totalTax = Math.round((totalFederalTax + stateTax) * 100) / 100;

    // ─── Rates ─────────────────────────────────────────────────────
    const effectiveRate = taxableIncome > 0
      ? Math.round((totalTax / taxableIncome) * 10000) / 100
      : 0;
    const marginalRate = getMarginalRate(taxableIncome, filingStatus);

    // ─── Section subtotals ─────────────────────────────────────────
    const sectionSubtotals: { [key: string]: number } = {
      total_income: totalIncome,
      total_adjustments: totalAdjustments,
      agi: agi,
      total_itemized_deductions: totalItemizedDeductions,
      total_additional_deductions: totalAdditionalDeductions,
      scha_total_state_local: totalStateLocal,
      scha_deductible_sl_tax: deductibleSL,
    };

    return {
      taxableIncome: Math.round(taxableIncome * 100) / 100,
      totalTax,
      federalTax: Math.round(totalFederalTax * 100) / 100,
      stateTax,
      effectiveRate,
      marginalRate,
      sectionSubtotals,
    };
  }

  /**
   * Sum specific field keys from a field map.
   */
  private sumFields(fields: { [key: string]: number }, keys: string[]): number {
    let sum = 0;
    for (const key of keys) {
      sum += fields[key] || 0;
    }
    return sum;
  }

  /**
   * Get state income tax rate using the existing StateRateService.
   * For Estate/Trust filing status, uses the fiduciary rate method.
   */
  private getStateRate(state: string, filingStatus: string, taxableIncome: number, taxYear: number): number {
    if (filingStatus === 'Estate' || filingStatus === 'Trust') {
      return this.stateRateService.getRatesForEstateAndTrust(state, filingStatus, taxableIncome, taxYear) || 0;
    }
    return this.stateRateService.getIndStateRate(state, filingStatus, taxableIncome, taxYear) || 0;
  }
}
